import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import LeadsTable from "./components/LeadsTable";
import FilterBar from "./components/FilterBar";
import TaskFilterModal from "./components/TaskFilterModal";
import PhoneServiceToggle from "./components/PhoneServiceToggle";
import useLeads from "./hooks/useLeads";
import useFilters from "./hooks/useFilters";
import { GoogleContactsProvider } from "./contexts/GoogleContactsContext";
import { PhoneServiceProvider } from "./contexts/PhoneServiceContext";
import { ContactsIntegrationProvider } from "./contexts/ContactsIntegrationContext";

const LeadsModular = () => {
  const [session, setSession] = useState(null);
  const [authorized, setAuthorized] = useState(true); // Default to true for now
  const [taskFilterModalOpen, setTaskFilterModalOpen] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(false);
  const loadingRef = useRef(null);

  const { 
    filters, 
    setFilters, 
    clearFilters,
    sortConfig, 
    setSortConfig
  } = useFilters();

  const { 
    leads, 
    isLoading,
    hasMore,
    loadMore,
    totalCount,
    commonTaskNames,
    allTaskNames,
    updateStatus,
    updateLeadPriority,
    getHighestPriority,
    toggleTaskComplete,
    updateDetails,
    handleDetailsChange,
    editedDetails,
    setEditedDetails,
    editingTasks,
    setEditingTasks,
    addTask,
    saveTaskChanges,
    updateProviders,
    editingProviders,
    setEditingProviders
  } = useLeads(true, filters, sortConfig);

  // Directly use leads from the hook since filtering is done at database level
  const filteredAndSortedLeads = leads;

  // Intersection Observer setup for infinite scrolling
  const observer = useRef();
  const lastLeadElementRef = useCallback(node => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore, loadMore]);

  useEffect(() => {
    const checkAuthorization = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      
      if (session) {
        // Check if user has role 1 in the roles table
        const { data, error } = await supabase
          .from('roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();
        
        if (error) {
          console.error('Error checking user role:', error);
          setAuthorized(false);
          return;
        }
        
        // Allow access if user has role 1
        setAuthorized(data && data.role === 1);
      } else {
        setAuthorized(false);
      }
    };

    checkAuthorization();
  }, []);

  // Copy current URL to clipboard for sharing
  const handleShareFilters = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        setShareFeedback(true);
        setTimeout(() => setShareFeedback(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
        alert('Failed to copy URL to clipboard');
      });
  };

  if (!authorized) {
    return <div className="w-full px-4 py-8">Not authorized to view this page.</div>;
  }

  return (
    <GoogleContactsProvider>
      <PhoneServiceProvider>
        <ContactsIntegrationProvider>
          <div className="w-full px-4 py-6" style={{ maxWidth: '100vw' }}>
            <div className="mb-6 flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
              <div className="flex items-center space-x-4">
                <PhoneServiceToggle />
                {filteredAndSortedLeads.length > 0 && (
                  <div className="text-sm text-gray-500">
                    Showing {filteredAndSortedLeads.length} leads
                    {totalCount > 0 && ` of ${totalCount} total`}
                  </div>
                )}
                {shareFeedback && (
                  <div className="text-sm text-green-500 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    URL copied to clipboard
                  </div>
                )}
                <button 
                  onClick={handleShareFilters}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="-ml-0.5 mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                  </svg>
                  Share Filters
                </button>
                {debugMode && (
                  <div>
                    <button 
                      onClick={() => console.log('Current Filters:', filters)}
                      className="px-3 py-2 text-xs bg-gray-100 rounded"
                    >
                      Log Filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            <FilterBar 
              filters={filters} 
              setFilters={setFilters} 
              setTaskFilterModalOpen={setTaskFilterModalOpen}
            />

            <div className="mt-6 bg-white rounded-lg shadow">
              <div className="w-full">
                <LeadsTable 
                  leads={filteredAndSortedLeads}
                  sortConfig={sortConfig} 
                  onSort={(field) => {
                    setSortConfig(prev => ({
                      field,
                      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
                    }));
                  }}
                  filters={filters}
                  commonTaskNames={commonTaskNames}
                  updateStatus={updateStatus}
                  updateLeadPriority={updateLeadPriority}
                  getHighestPriority={getHighestPriority}
                  toggleTaskComplete={toggleTaskComplete}
                  updateDetails={updateDetails}
                  handleDetailsChange={handleDetailsChange}
                  editedDetails={editedDetails}
                  setEditedDetails={setEditedDetails}
                  editingTasks={editingTasks}
                  setEditingTasks={setEditingTasks}
                  addTask={addTask}
                  saveTaskChanges={saveTaskChanges}
                  updateProviders={updateProviders}
                  editingProviders={editingProviders}
                  setEditingProviders={setEditingProviders}
                  lastLeadRef={lastLeadElementRef}
                />
                
                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex justify-center items-center p-4" ref={loadingRef}>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                )}
                
                {/* End of list indicator */}
                {!hasMore && leads.length > 0 && (
                  <div className="text-center text-gray-500 p-4">
                    No more leads to load
                  </div>
                )}
              </div>
            </div>
            
            <TaskFilterModal
              open={taskFilterModalOpen}
              setOpen={setTaskFilterModalOpen}
              filters={filters}
              setFilters={setFilters}
              leads={leads}
              allTaskNames={allTaskNames}
            />
            
            {/* Feedback message container */}
            <div id="feedback-message" className="hidden fixed bottom-4 right-4 z-50 px-4 py-2 rounded shadow-md"></div>
            
            {/* Debug mode toggle */}
            <button 
              onClick={() => setDebugMode(!debugMode)}
              className="text-xs text-gray-500 hover:text-gray-700 underline fixed bottom-4 left-4"
            >
              {debugMode ? 'Hide Debug Info' : 'Show Debug Info'}
            </button>
            
            {debugMode && (
              <div className="text-xs bg-gray-100 p-4 my-2 rounded fixed bottom-10 left-4 z-50 max-w-sm overflow-auto max-h-80">
                <h3 className="font-bold mb-2">Debug Information</h3>
                <div>Total leads: {totalCount}</div>
                <div>Loaded leads: {leads.length}</div>
                <div>All task names: {allTaskNames.length}</div>
                <div>Loading state: {isLoading ? 'Loading' : 'Not loading'}</div>
                <div>Has more: {hasMore ? 'Yes' : 'No'}</div>
                <div>Active filters: {JSON.stringify(filters)}</div>
                <div>Sort config: {JSON.stringify(sortConfig)}</div>
                <div>Common task names: {JSON.stringify(commonTaskNames)}</div>
              </div>
            )}
          </div>
        </ContactsIntegrationProvider>
      </PhoneServiceProvider>
    </GoogleContactsProvider>
  );
};

export default LeadsModular; 